/** A row as `/subscriptions/my/history` returns it (newest first). */
export interface SubscriptionHistoryRow {
  id: string;
  status?: string | null;
}

/**
 * The subscription whose cancellation the "Your subscription was cancelled"
 * banners should announce — or `null` when there is nothing to announce.
 *
 * Two rules: it must be the newest CANCELLED row, AND the account must hold no
 * live subscription (ACTIVE or PAST_DUE).
 *
 * The three banners (global, account dashboard, account subscription) each tested
 * `history[0].status === "CANCELLED"` on their own. That is wrong whenever a
 * closed row is newer than the live one — and that is routine: a declined or
 * abandoned checkout closes as CANCELLED and lands at the top of the history. A
 * paying subscriber was then told their subscription had been cancelled and they
 * were on the Free plan, site-wide. The comment above the dashboard banner even
 * said "and there's no active/past-due sub" — the code never checked it.
 *
 * `INCOMPLETE` rows (a checkout in flight, not yet paid) are ignored: they are
 * neither a live subscription nor a cancellation.
 */
export const findAnnouncableCancelledSub = (
  rows: unknown,
): SubscriptionHistoryRow | null => {
  const subs: SubscriptionHistoryRow[] = Array.isArray(rows) ? rows : [];
  const hasLive = subs.some((s) => s?.status === "ACTIVE" || s?.status === "PAST_DUE");
  if (hasLive) return null;
  return subs.find((s) => s?.status === "CANCELLED") ?? null;
};
